import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart,
  MessageCircle,
  Share2,
  MoreHorizontal,
  Plus,
  Camera,
  X,
  Trash2,
  AlertTriangle,
  Image as ImageIcon,
  Users,
  Search,
  UserPlus,
  UserMinus,
  MapPin,
  Flag,
  Trophy,
  Calendar,
  ChevronRight,
  Link as LinkIcon,
  Send,
  Volume2,
  VolumeX,
  Home,
  RotateCcw,
  ArrowDown,
  Play,
  ChevronUp
} from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { StoryViewer, CreateStoryModal } from "./StoryViewer";
import { Input } from "./ui/input";
import { GolfShotsModal } from "./GolfShots";
import { ShotTracerModal } from "./ShotTracer";
import UserBubble, { BUBBLE_COLORS } from "./UserBubble";
import { PostContentWithEmbeds } from "./SocialEmbed";
import { EventFeedLink } from './events/EventFeedLink';
import VideoReelViewer from "./VideoReelViewer";
import ImageGalleryViewer from "./ImageGalleryViewer";
import PWAInstallPrompt from "./PWAInstallPrompt";

if (!process.env.REACT_APP_BACKEND_URL) throw new Error('REACT_APP_BACKEND_URL is required');
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format time ago - used across components
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Hook Feed Overlay - Reels-style viewer for posts with media
const HookFeedOverlay = ({ posts, user, onClose, onLike }) => {
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [liked, setLiked] = useState({});
  const videoRef = useRef(null);
  const touchStartY = useRef(0);

  // Build slides from posts with media
  useEffect(() => {
    const mediaSlides = [];
    (posts || []).forEach(post => {
      const postId = post._id || post.id;
      if (post.video_url) {
        mediaSlides.push({
          id: `${postId}_video`,
          postId,
          type: 'video',
          url: post.video_url,
          content: post.content || '',
          authorName: post.author_name || 'Golfer',
          authorAvatar: post.author_avatar,
          likes: post.likes || [],
          comments: post.comments || []
        });
      }
      if (post.media_urls && post.media_urls.length > 0) {
        post.media_urls.forEach((url, idx) => {
          if (url) {
            mediaSlides.push({
              id: `${postId}_img_${idx}`,
              postId,
              type: 'image',
              url,
              content: post.content || '',
              authorName: post.author_name || 'Golfer',
              authorAvatar: post.author_avatar,
              likes: post.likes || [],
              comments: post.comments || []
            });
          }
        });
      }
    });
    setSlides(mediaSlides);
  }, [posts]);

  // Video control
  useEffect(() => {
    if (videoRef.current && slides[currentIndex]?.type === 'video') {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, currentIndex, slides]);

  const goNext = () => {
    if (currentIndex < slides.length - 1) {
      setCurrentIndex(i => i + 1);
    } else if (slides.length > 0) {
      setShowEnd(true);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(i => i - 1);
  };

  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    if (showEnd) return;
    const delta = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(delta) > 50) {
      delta < 0 ? goNext() : goPrev();
    }
  };

  const handleLike = (postId) => {
    setLiked(prev => ({ ...prev, [postId]: !prev[postId] }));
    onLike?.(postId);
    toast.success('Liked!');
  };

  const handleShare = (slide) => {
    const url = `${window.location.origin}/post/${slide.postId}`;
    if (navigator.share) {
      navigator.share({ title: 'Check this out!', url });
    } else {
      navigator.clipboard?.writeText(url);
      toast.success('Link copied!');
    }
  };

  const S = {
    container: { position: 'fixed', inset: 0, backgroundColor: '#000', color: '#fff', zIndex: 9999, fontFamily: 'system-ui' },
    center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', padding: 20 },
    btn: { padding: '14px 32px', backgroundColor: '#22c55e', border: 'none', borderRadius: 999, color: '#fff', fontWeight: 'bold', fontSize: 16, cursor: 'pointer', marginTop: 16 },
    btnOutline: { padding: '14px 32px', backgroundColor: 'transparent', border: '2px solid #fff', borderRadius: 999, color: '#fff', fontWeight: 'bold', fontSize: 16, cursor: 'pointer', marginTop: 12 }
  };

  // No media posts
  if (slides.length === 0) {
    return (
      <div style={S.container}>
        <div style={S.center}>
          <div>
            <div style={{ fontSize: 64, marginBottom: 16 }}>👁️</div>
            <h1 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 8 }}>NO POSTS YET!</h1>
            <p style={{ opacity: 0.6 }}>Be the first to share a photo or video.</p>
            <button style={S.btn} onClick={onClose}>Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  // End of feed
  if (showEnd) {
    return (
      <div style={S.container}>
        <div style={S.center}>
          <div>
            <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
            <h1 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 8 }}>You've seen it all!</h1>
            <p style={{ opacity: 0.6, marginBottom: 24 }}>{slides.length} posts viewed</p>
            <button style={S.btn} onClick={() => { setShowEnd(false); setCurrentIndex(0); }}>
              <RotateCcw style={{ width: 20, height: 20, display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
              SWING (Loop)
            </button>
            <br />
            <button style={S.btnOutline} onClick={onClose}>
              <ArrowDown style={{ width: 20, height: 20, display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
              DROP (Home)
            </button>
          </div>
        </div>
      </div>
    );
  }

  const slide = slides[currentIndex];

  return (
    <div 
      style={S.container} 
      onTouchStart={handleTouchStart} 
      onTouchEnd={handleTouchEnd}
      onClick={() => setShowControls(!showControls)}
    >
      {/* Media */}
      {slide.type === 'video' ? (
        <video
          ref={videoRef}
          src={slide.url}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          autoPlay loop muted={isMuted} playsInline
        />
      ) : (
        <div style={{ 
          width: '100%', height: '100%', 
          backgroundImage: `url(${slide.url})`, 
          backgroundSize: 'cover', 
          backgroundPosition: 'center' 
        }} />
      )}

      {/* Swipe hint on first */}
      {currentIndex === 0 && !showControls && (
        <div style={{ ...S.center, position: 'absolute', top: 0, pointerEvents: 'none' }}>
          <div style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: 24, borderRadius: 16 }}>
            <ChevronUp style={{ width: 32, height: 32, margin: '0 auto 8px', animation: 'bounce 1s infinite' }} />
            <p style={{ fontWeight: 'bold' }}>Swipe up</p>
            <p style={{ fontSize: 12, opacity: 0.6 }}>Tap for controls</p>
            <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}`}</style>
          </div>
        </div>
      )}

      {/* Controls */}
      {showControls && (
        <>
          {/* Top bar */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)', zIndex: 10 }}>
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ padding: 8, backgroundColor: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', color: '#fff' }}>
              <X style={{ width: 20, height: 20 }} />
            </button>
            <span style={{ fontWeight: 'bold', fontSize: 18 }}>HOOK</span>
            <span style={{ fontSize: 12, backgroundColor: 'rgba(0,0,0,0.5)', padding: '4px 12px', borderRadius: 99 }}>{currentIndex + 1}/{slides.length}</span>
          </div>

          {/* Bottom info */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 70, padding: 16, paddingBottom: 32, background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              {slide.authorAvatar ? (
                <img src={slide.authorAvatar} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '2px solid #22c55e' }} />
              ) : (
                <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                  {slide.authorName?.charAt(0) || '?'}
                </div>
              )}
              <span style={{ fontWeight: 'bold' }}>{slide.authorName}</span>
            </div>
            {slide.content && <p style={{ fontSize: 14, opacity: 0.9 }}>{slide.content.slice(0, 100)}</p>}
          </div>

          {/* Side buttons */}
          <div style={{ position: 'absolute', right: 12, bottom: 100, display: 'flex', flexDirection: 'column', gap: 16, zIndex: 10 }}>
            {slide.type === 'video' && (
              <button onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }} style={{ padding: 12, backgroundColor: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', color: '#fff' }}>
                {isMuted ? <VolumeX style={{ width: 24, height: 24 }} /> : <Volume2 style={{ width: 24, height: 24 }} />}
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); handleLike(slide.postId); }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'none', border: 'none', color: '#fff' }}>
              <div style={{ padding: 12, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '50%' }}>
                <Heart style={{ width: 24, height: 24, fill: liked[slide.postId] ? '#ef4444' : 'none', color: liked[slide.postId] ? '#ef4444' : '#fff' }} />
              </div>
              <span style={{ fontSize: 12, marginTop: 4 }}>{slide.likes?.length || 0}</span>
            </button>
            <button style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'none', border: 'none', color: '#fff' }}>
              <div style={{ padding: 12, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '50%' }}>
                <MessageCircle style={{ width: 24, height: 24 }} />
              </div>
              <span style={{ fontSize: 12, marginTop: 4 }}>{slide.comments?.length || 0}</span>
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleShare(slide); }} style={{ padding: 12, backgroundColor: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', color: '#fff' }}>
              <Share2 style={{ width: 24, height: 24 }} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ padding: 12, backgroundColor: '#22c55e', border: 'none', borderRadius: '50%', color: '#fff' }}>
              <Home style={{ width: 24, height: 24 }} />
            </button>
          </div>
        </>
      )}

      {/* Progress */}
      <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', width: 4, height: 40, backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 99, zIndex: 5 }}>
        <div style={{ width: '100%', height: `${((currentIndex + 1) / slides.length) * 100}%`, backgroundColor: '#22c55e', borderRadius: 99, transition: 'height 0.3s' }} />
      </div>
    </div>
  );
};

// Helper function to get glow class based on engagement status
const getEngagementGlowClass = (entity) => {
  if (entity?.engagement_status === 'chatting' || entity?.is_chatting) {
    return 'chatting-glow';
  } else if (entity?.engagement_status === 'online' || entity?.is_online) {
    return 'online-glow';
  }
  return '';
};

// User Profile Modal - Shows detailed user info when tapping on avatar
const UserProfileModal = ({ isOpen, onClose, member, onFollow, onMessage, currentUserId }) => {
  const [profileStats, setProfileStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showBuddiesList, setShowBuddiesList] = useState(false);
  const [buddies, setBuddies] = useState([]);
  const [loadingBuddies, setLoadingBuddies] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (isOpen && member?.user_id) {
      fetchProfileStats();
      setShowBuddiesList(false); // Reset when modal opens
    }
  }, [isOpen, member?.user_id]);
  
  const fetchProfileStats = async () => {
    if (!member?.user_id) return;
    setLoadingStats(true);
    try {
      // Fetch the member's public profile stats
      const response = await axios.get(`${API}/users/${member.user_id}/public-profile`);
      setProfileStats(response.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      // Use member data as fallback
      setProfileStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchBuddies = async () => {
    if (!member?.user_id) return;
    setLoadingBuddies(true);
    try {
      const response = await axios.get(`${API}/users/${member.user_id}/buddies`);
      setBuddies(response.data.buddies || []);
    } catch (error) {
      console.error("Error fetching buddies:", error);
      toast.error("Failed to load buddies");
    } finally {
      setLoadingBuddies(false);
    }
  };

  const handleShowBuddies = () => {
    setShowBuddiesList(true);
    fetchBuddies();
  };

  const handleBuddyClick = (buddyUserId) => {
    onClose();
    navigate(`/profile/${buddyUserId}`);
  };
  
  if (!member) return null;
  
  const isSelf = member.user_id === currentUserId;
  const stats = profileStats || member;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="ps-themed-dialog sm:max-w-md p-0 overflow-hidden" data-testid="feed-profile-dialog" style={{
        background: 'linear-gradient(180deg, var(--ps-bg-elevated), var(--ps-bg-surface))',
        border: '1px solid var(--ps-border)'
      }}>
        {/* Cover/Header Area */}
        <div className="relative h-24 bg-gradient-to-br from-[var(--ps-primary)] via-green-600 to-green-800">
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Profile Photo - Overlapping with bubble color border - CLICKABLE AVATAR */}
        <div className="px-4 -mt-12 relative z-10">
          <div className="relative inline-block">
            {(() => {
              const colorConfig = BUBBLE_COLORS[member.bubble_color] || BUBBLE_COLORS.green;
              const baseShadow = '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)';
              return (
                <button 
                  className="w-24 h-24 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#1a1a1a] transition-all hover:scale-110 active:scale-95"
                  onClick={() => {
                    onClose();
                    navigate(`/profile/${member.user_id}`);
                  }}
                  aria-label={`View ${member.name}'s full profile`}
                  style={{
                    background: 'linear-gradient(145deg, var(--ps-bg-elevated), var(--ps-bg-surface))',
                    boxShadow: member.is_online 
                      ? `${colorConfig.glow}, ${baseShadow}` 
                      : baseShadow,
                    border: `3px solid ${colorConfig.color}`,
                    transition: 'box-shadow 0.3s ease, transform 0.15s ease'
                  }}
                >
                  {member.picture ? (
                    <img src={member.picture} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-[var(--ps-primary)]">
                      {member.name?.charAt(0) || 'U'}
                    </div>
                  )}
                </button>
              );
            })()}
          </div>
        </div>
        
        {/* User Info */}
        <div className="px-4 pt-2 pb-4">
          <div className="flex items-center gap-2 mb-1">
            <h2 className={`text-xl font-bold text-white ${getEngagementGlowClass(member)}`}>{member.name}</h2>
            {(member.handicap !== null && member.handicap !== undefined) || (stats?.stats?.handicap !== null && stats?.stats?.handicap !== undefined) ? (
              <span className="text-xs bg-[var(--ps-primary)] text-white px-2 py-0.5 rounded-full font-semibold">
                {member.handicap === undefined ? stats?.stats?.handicap ?? 'Not established' : member.handicap === null ? 'Not established' : member.handicap.toFixed(1)} HCP
              </span>
            ) : null}
          </div>
          
          {member.username && (
            <p className="text-gray-400 text-sm mb-2">@{member.username}</p>
          )}
          
          {(stats?.home_course || member.home_course) && (
            <p className="text-gray-300 text-sm mb-3 flex items-center justify-center gap-1">
              <Flag className="w-3 h-3 text-[var(--ps-primary)]" />
              {stats?.home_course || member.home_course}
            </p>
          )}
          
          {/* Stats Row - Buddies clickable */}
          <div className="flex items-center gap-4 mb-4">
            <button 
              className="text-center hover:opacity-80 transition-opacity"
              onClick={handleShowBuddies}
              data-testid="buddies-stat-btn"
            >
              <p className="text-lg font-bold text-white">{stats?.buddies_count ?? member.following_count ?? 0}</p>
              <p className="text-xs text-gray-500 underline">Buddies</p>
            </button>
            <div className="text-center">
              <p className="text-lg font-bold text-white">{stats?.rounds_count || stats?.stats?.rounds_played || stats?.rounds_played || '--'}</p>
              <p className="text-xs text-gray-500">Rounds</p>
            </div>
            {stats?.mutual_buddies > 0 && (
              <div className="text-center">
                <p className="text-lg font-bold text-[var(--ps-primary)]">{stats?.mutual_buddies}</p>
                <p className="text-xs text-gray-500">Mutual</p>
              </div>
            )}
          </div>
          
          {/* Buddies List View - shows when Buddies is clicked */}
          {showBuddiesList && (
            <div className="mb-4 bg-gray-900/50 rounded-xl p-3 border border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Users className="w-4 h-4 text-[var(--ps-primary)]" />
                  {member.name}'s Golf Buddies
                </h4>
                <button 
                  onClick={() => setShowBuddiesList(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {loadingBuddies ? (
                <div className="flex justify-center py-4">
                  <div className="w-5 h-5 border-2 border-[var(--ps-primary)] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : buddies.length === 0 ? (
                <p className="text-center text-gray-500 text-sm py-2">No golf buddies yet</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {buddies.map((buddy) => (
                    <button
                      key={buddy.user_id}
                      onClick={() => handleBuddyClick(buddy.user_id)}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800/50 transition-colors text-left"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        buddy.picture ? '' : 'bg-[var(--ps-primary)] text-white'
                      }`} style={{
                        border: `2px solid ${BUBBLE_COLORS[buddy.bubble_color]?.color || BUBBLE_COLORS.green.color}`
                      }}>
                        {buddy.picture ? (
                          <img src={buddy.picture} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          buddy.name?.charAt(0) || 'G'
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{buddy.name}</p>
                        {buddy.handicap !== null && buddy.handicap !== undefined && (
                          <p className="text-xs text-[var(--ps-primary)]">{buddy.handicap} HCP</p>
                        )}
                      </div>
                      {buddy.is_following && (
                        <span className="text-[10px] text-[var(--ps-primary)] bg-[var(--ps-primary)]/20 px-1.5 py-0.5 rounded">
                          Mutual
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Action Buttons */}
          {!isSelf && (
            <div className="space-y-2 mb-4">
              <div className="flex gap-2">
                <Button
                  onClick={() => onFollow?.(member.user_id, member.is_following)}
                  variant={member.is_following ? "outline" : "default"}
                  className={`flex-1 ${member.is_following ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'bg-[var(--ps-primary)] hover:bg-green-600 text-white'}`}
                >
                  {member.is_following ? (
                    <><UserMinus className="w-4 h-4 mr-2" /> Buddies</>
                  ) : (
                    <><UserPlus className="w-4 h-4 mr-2" /> Add Buddy</>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    onMessage?.(member);
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Message
                </Button>
              </div>
            </div>
          )}
          
          {/* Details Section */}
          <div className="border-t border-gray-800 pt-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Details</h3>
            
            {member.home_course && (
              <div className="flex items-center gap-3 text-gray-300">
                <Flag className="w-5 h-5 text-[var(--ps-primary)]" />
                <div>
                  <p className="text-xs text-gray-500">Home Course</p>
                  <p className="text-sm">{member.home_course}</p>
                </div>
              </div>
            )}
            
            {member.location && (
              <div className="flex items-center gap-3 text-gray-300">
                <MapPin className="w-5 h-5 text-[var(--ps-primary)]" />
                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="text-sm">{member.location}</p>
                </div>
              </div>
            )}
            
            {stats?.best_score && (
              <div className="flex items-center gap-3 text-gray-300">
                <Trophy className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-xs text-gray-500">Best Score</p>
                  <p className="text-sm">{stats.best_score}</p>
                </div>
              </div>
            )}
            
            {stats?.average_score && (
              <div className="flex items-center gap-3 text-gray-300">
                <Target className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-xs text-gray-500">Average Score</p>
                  <p className="text-sm">{stats.average_score.toFixed(1)}</p>
                </div>
              </div>
            )}
            
            {member.member_since && (
              <div className="flex items-center gap-3 text-gray-300">
                <Calendar className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Member Since</p>
                  <p className="text-sm">{new Date(member.member_since).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* View Full Profile Link */}
          {!isSelf && (
            <button 
              onClick={() => {
                onClose();
                // Navigate to full profile page if it exists
                navigate(`/profile/${member.user_id}`);
              }}
              className="w-full mt-4 py-2 text-center text-[var(--ps-primary)] text-sm font-medium hover:bg-gray-800/50 rounded-lg transition-colors flex items-center justify-center gap-1"
            >
              View Full Profile <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Target icon for average score
const Target = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

// Feed tabs - Clean text only (Messenger moved to dedicated Chat page)
const feedTabs = [
  { id: 'feed', label: '19th' },
  { id: 'members', label: 'Members' },
];

// Compact User Stats Widget - Shows handicap and stats in a compact format
const CompactStatsWidget = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/handicap`);
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex-1 bg-[var(--ps-bg-surface)] rounded-xl p-3 border border-[var(--ps-border)] animate-pulse">
        <div className="h-8 bg-[var(--ps-bg-elevated)] rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-[var(--ps-bg-elevated)] rounded"></div>
      </div>
    );
  }

  const handicap = typeof stats?.handicap_index === 'number' && Number.isFinite(stats.handicap_index) ? stats.handicap_index.toFixed(1) : 'N/A';
  const roundsPlayed = stats?.rounds_played || 0;
  const average = stats?.average_score || '--';
  const bestScore = stats?.best_score || '--';
  const worstScore = stats?.worst_score || '--';

  return (
    <div 
      className="flex-1 bg-[var(--ps-bg-surface)] rounded-xl p-3 border border-[var(--ps-border)]"
      data-testid="compact-stats-widget"
    >
      {/* Handicap Row - Links to Add Score */}
      <div 
        className="flex items-center justify-between mb-2 cursor-pointer hover:bg-[var(--ps-bg-hover)] -mx-3 -mt-3 px-3 pt-3 pb-2 rounded-t-xl transition-colors"
        onClick={() => navigate('/add-score')}
      >
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[var(--ps-primary)] to-emerald-600 flex items-center justify-center">
            <span className="text-white font-bold text-lg">{typeof handicap === 'number' ? handicap.toFixed(1) : handicap}</span>
          </div>
          <div>
            <p className="text-[var(--ps-text-muted)] text-xs">Handicap</p>
            <p className="text-[var(--ps-text-primary)] text-sm font-medium">Index</p>
          </div>
        </div>
        <Plus className="w-5 h-5 text-[var(--ps-primary)]" />
      </div>
      
      {/* Stats Row - Links to History */}
      <div 
        className="flex justify-between text-center border-t border-[var(--ps-border)] pt-2 cursor-pointer hover:bg-[var(--ps-bg-hover)] -mx-3 -mb-3 px-3 pb-3 rounded-b-xl transition-colors"
        onClick={() => navigate('/history')}
      >
        <div>
          <p className="text-[var(--ps-text-muted)] text-[10px] uppercase">Rnds</p>
          <p className="text-[var(--ps-text-primary)] text-sm font-semibold">{roundsPlayed}</p>
        </div>
        <div>
          <p className="text-[var(--ps-text-muted)] text-[10px] uppercase">Avg</p>
          <p className="text-[var(--ps-text-primary)] text-sm font-semibold">{typeof average === 'number' ? average.toFixed(0) : average}</p>
        </div>
        <div>
          <p className="text-[var(--ps-primary)] text-[10px] uppercase">Best</p>
          <p className="text-[var(--ps-primary)] text-sm font-semibold">{bestScore}</p>
        </div>
        <div>
          <p className="text-red-400 text-[10px] uppercase">Worst</p>
          <p className="text-red-400 text-sm font-semibold">{worstScore}</p>
        </div>
      </div>
    </div>
  );
};

// Stories Component
const StoriesRow = ({ user, userStories, onCreateStory, onViewStory }) => {
  // Check if current user has active stories
  const myStories = userStories?.find(us => us.user_id === user?.user_id);
  const hasMyStory = myStories?.stories?.length > 0;
  
  // Filter out current user AND users with empty stories arrays
  const otherStories = userStories?.filter(us => 
    us.user_id !== user?.user_id && us.stories?.length > 0
  ) || [];
  
  return (
    <div className="stories-container" data-testid="stories-row">
      {/* Create Story / Your Story */}
      <div 
        className="story-item" 
        data-testid="create-story-btn"
        onClick={() => hasMyStory ? onViewStory?.(user?.user_id) : onCreateStory()}
      >
        <div className={`story-avatar-wrapper ${hasMyStory ? '' : 'create'}`}>
          {hasMyStory ? (
            <Avatar className="story-avatar">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
          ) : (
            <div className="story-avatar flex items-center justify-center bg-[var(--ps-bg-elevated)]">
              <Plus className="w-6 h-6 text-[var(--ps-text-muted)]" />
            </div>
          )}
        </div>
        <span className="story-name">{hasMyStory ? 'Your Story' : 'Add Story'}</span>
      </div>

      {/* Other Users' Stories */}
      {otherStories.map((userStory) => (
        <div 
          key={userStory.user_id} 
          className="story-item"
          onClick={() => onViewStory?.(userStory.user_id)}
          data-testid={`story-${userStory.user_id}`}
        >
          <div className={`story-avatar-wrapper ${userStory.has_unseen ? '' : 'no-story'}`}>
            <Avatar className="story-avatar">
              <AvatarImage src={userStory.user_picture} />
              <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
                {userStory.user_name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
          </div>
          <span className="story-name">{userStory.user_name?.split(' ')[0]}</span>
        </div>
      ))}
    </div>
  );
};

// Post Card Component
const PostCard = ({ post, user, onLike, onComment, onDelete, onReport, onViewProfile, onVideoClick, onImageClick }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment?.(post.post_id, commentText);
    setCommentText('');
    setSubmitting(false);
  };

  const getCategoryColor = (type) => {
    const colors = {
      general: 'text-[var(--ps-primary)]',
      round_update: 'text-[var(--ps-success)]',
      looking_for_group: 'text-[var(--ps-info)]',
      course_review: 'text-[var(--ps-warning)]',
      buy_sell: 'text-purple-400'
    };
    return colors[type] || colors.general;
  };

  const getCategoryLabel = (type) => {
    const labels = {
      general: 'General',
      round_update: 'Round Update',
      looking_for_group: 'Looking for Group',
      course_review: 'Course Review',
      buy_sell: 'For Sale'
    };
    return labels[type] || 'General';
  };

  return (
    <article className="feed-post animate-fade-in" data-testid={`post-${post.post_id}`}>
      {/* Header */}
      <div className="feed-post-header">
        <div className="feed-post-user">
          {/* Avatar is the ONLY clickable link to profile */}
          <UserBubble
            userId={post.user_id}
            picture={post.user_picture}
            name={post.user_name}
            bubbleColor={post.bubble_color || 'green'}
            isOnline={post.is_online}
            size="lg"
            onClick={() => onViewProfile?.(post.user_id)}
          />
          <div className="feed-post-meta">
            {/* Name is clickable - navigates to profile like avatar */}
            <div 
              className={`feed-post-name text-left ${getEngagementGlowClass(post)} cursor-pointer hover:underline`}
              onClick={() => onViewProfile?.(post.user_id)}
              data-testid={`post-author-name-${post.post_id}`}
            >
              {post.user_name}
              {post.user_handicap !== null && (
                <span className="handicap-badge ml-2">
                  {post.user_handicap} HCP
                </span>
              )}
            </div>
            <div className="feed-post-details">
              <span>{formatTimeAgo(post.created_at)}</span>
              <span>•</span>
              <span className={getCategoryColor(post.post_type)}>
                {getCategoryLabel(post.post_type)}
              </span>
              {post.visibility === 'buddies_only' && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1 text-amber-500">
                    <Users className="w-3 h-3" />
                    Buddies
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <button 
          className="p-2 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-muted)]"
          onClick={() => post.user_id === user?.user_id ? onDelete?.(post.post_id) : onReport?.(post)}
        >
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </div>

      {/* Content - with social media embed support */}
      <div className="feed-post-content">
        <PostContentWithEmbeds 
          content={post.content} 
          onVideoClick={onVideoClick}
          postData={post}
        />
        {post.shared_event_id && <EventFeedLink eventId={post.shared_event_id} postId={post.post_id} />}
      </div>

      {/* Image/Video - Clickable for fullscreen gallery */}
      {post.image_url && (
        <div 
          className="cursor-pointer relative group"
          onClick={() => !post.media_type?.includes('video') && onImageClick?.({
            images: [{ url: post.image_url, caption: post.content }],
            initialIndex: 0,
            postInfo: {
              author: { name: post.user_name, picture: post.user_picture },
              likes: post.likes_count,
              comments: post.comments_count,
              timestamp: formatTimeAgo(post.created_at)
            }
          })}
          data-testid={`post-media-${post.post_id}`}
        >
          {(post.media_type === 'video' || post.image_url.includes('/videos/') || post.image_url.endsWith('.mp4') || post.image_url.endsWith('.mov')) ? (
            <video 
              src={post.image_url} 
              className="feed-post-image"
              controls
              playsInline
              preload="metadata"
            />
          ) : (
            <img 
              src={post.image_url} 
              alt="Post" 
              className="feed-post-image"
              loading="lazy"
            />
          )}
        </div>
      )}

      {/* Reactions Bar */}
      <div className="feed-post-reactions">
        <div className="flex items-center gap-2">
          {post.likes_count > 0 && (
            <>
              <div className="reaction-avatars">
                {/* Show up to 3 reaction avatars */}
                {[...Array(Math.min(3, post.likes_count))].map((_, i) => (
                  <div 
                    key={i}
                    className="w-6 h-6 rounded-full bg-[var(--ps-bg-elevated)] border-2 border-[var(--ps-bg-surface)] flex items-center justify-center"
                    style={{ marginLeft: i > 0 ? '-8px' : 0 }}
                  >
                    <Heart className="w-3 h-3 text-red-500 fill-current" />
                  </div>
                ))}
              </div>
              <span>{post.likes_count} reaction{post.likes_count !== 1 ? 's' : ''}</span>
            </>
          )}
        </div>
        {post.comments_count > 0 && (
          <button 
            className="hover:underline"
            onClick={() => setShowComments(!showComments)}
          >
            {post.comments_count} comment{post.comments_count !== 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="feed-post-actions">
        <button 
          className={`post-action-btn ${post.is_liked ? 'liked' : ''}`}
          onClick={() => onLike?.(post.post_id)}
          data-testid={`like-btn-${post.post_id}`}
        >
          <Heart className="w-5 h-5" />
          <span>Like</span>
        </button>
        <button 
          className="post-action-btn"
          onClick={() => setShowComments(!showComments)}
          data-testid={`comment-btn-${post.post_id}`}
        >
          <MessageCircle className="w-5 h-5" />
          <span>Comment</span>
        </button>
        <button 
          className="post-action-btn"
          onClick={() => {
            navigator.share?.({ 
              title: 'Par Seeker Post', 
              text: post.content?.substring(0, 100),
              url: window.location.href 
            }).catch(() => {});
          }}
          data-testid={`share-btn-${post.post_id}`}
        >
          <Share2 className="w-5 h-5" />
          <span>Share</span>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="p-4 border-t border-[var(--ps-border)] space-y-3">
          {/* Existing Comments */}
          {post.comments?.map((comment) => (
            <div key={comment.comment_id} className="flex gap-3">
              <Avatar 
                className="w-8 h-8 flex-shrink-0 cursor-pointer"
                onClick={() => onViewProfile?.(comment.user_id)}
              >
                <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)] text-xs">
                  {comment.user_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 bg-[var(--ps-bg-hover)] rounded-xl p-3">
                <span 
                  className="font-semibold text-sm text-[var(--ps-text-primary)] cursor-pointer hover:underline"
                  onClick={() => onViewProfile?.(comment.user_id)}
                  data-testid={`comment-author-name-${comment.comment_id}`}
                >
                  {comment.user_name}
                </span>
                <p className="text-sm text-[var(--ps-text-secondary)] mt-0.5">
                  {comment.content}
                </p>
              </div>
            </div>
          ))}

          {/* Add Comment */}
          <div className="flex gap-3 pt-2">
            <Avatar className="w-8 h-8 flex-shrink-0">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)] text-xs">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Write a comment..."
                className="dark-input flex-1 py-2 px-3 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment()}
              />
              <button
                onClick={handleSubmitComment}
                disabled={!commentText.trim() || submitting}
                className="dark-btn-primary py-2 px-3 text-sm"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

// Create Post Modal
const CreatePostModal = ({ isOpen, onClose, user, onSubmit, preloadedMedia }) => {
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showGolfShots, setShowGolfShots] = useState(false);
  const [showShotTracer, setShowShotTracer] = useState(false);
  const [visibility, setVisibility] = useState('public'); // public or buddies_only
  const [selectedTags, setSelectedTags] = useState([]);
  
  // Social media embed state
  const [embedPreview, setEmbedPreview] = useState(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState(null);
  
  // Available category tags for posts
  const categoryTags = [
    { id: 'equipment', name: 'Equipment', icon: '🏌️' },
    { id: 'instruction', name: 'Tips', icon: '📚' },
    { id: 'course_photos', name: 'Course Pics', icon: '📸' },
    { id: 'tournament', name: 'Tournament', icon: '🏆' },
    { id: 'handicap_goals', name: 'Goals', icon: '🎯' },
    { id: 'funny', name: 'Humor', icon: '😂' },
  ];
  
  // Detect social media URLs in content
  const detectSocialUrl = (text) => {
    const patterns = [
      { name: 'TikTok', pattern: /https?:\/\/(www\.)?(tiktok\.com\/@[\w.-]+\/video\/\d+|tiktok\.com\/t\/[\w]+|vm\.tiktok\.com\/[\w]+)/i },
      { name: 'YouTube', pattern: /https?:\/\/(www\.)?(youtube\.com\/watch\?v=[\w-]+|youtu\.be\/[\w-]+|youtube\.com\/shorts\/[\w-]+)/i },
      { name: 'Twitter', pattern: /https?:\/\/(www\.)?(twitter\.com\/\w+\/status\/\d+|x\.com\/\w+\/status\/\d+)/i },
      { name: 'Instagram', pattern: /https?:\/\/(www\.)?instagram\.com\/(p|reel|tv)\/[\w-]+/i },
      { name: 'Vimeo', pattern: /https?:\/\/(www\.)?vimeo\.com\/\d+/i },
    ];
    
    for (const { name, pattern } of patterns) {
      const match = text.match(pattern);
      if (match) {
        return { url: match[0], provider: name };
      }
    }
    return null;
  };
  
  // Fetch embed preview when URL is detected
  useEffect(() => {
    const detected = detectSocialUrl(content);
    
    if (detected && detected.url !== embedPreview?.original_url) {
      setEmbedLoading(true);
      setEmbedError(null);
      
      axios.post(`${API}/oembed/fetch`, null, { params: { url: detected.url } })
        .then(response => {
          if (response.data.supported && response.data.embed) {
            setEmbedPreview(response.data.embed);
          } else if (response.data.error) {
            setEmbedError(response.data.error);
            setEmbedPreview(null);
          } else {
            setEmbedPreview(null);
          }
        })
        .catch(() => {
          setEmbedError("Couldn't load preview");
          setEmbedPreview(null);
        })
        .finally(() => setEmbedLoading(false));
    } else if (!detected) {
      setEmbedPreview(null);
      setEmbedError(null);
    }
  }, [content]);
  
  // Handle preloaded media from Quick Post - set immediately when we get it
  useEffect(() => {
    if (preloadedMedia?.url) {
      setImage(preloadedMedia.url);
      setImagePreview(preloadedMedia.url);
    }
    // Handle shared text content from PWA Share Target
    if (preloadedMedia?.sharedText) {
      setContent(preloadedMedia.sharedText);
    }
  }, [preloadedMedia]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setContent('');
      setImage(null);
      setImagePreview(null);
      setVisibility('public');
      setSelectedTags([]);
      setEmbedPreview(null);
      setEmbedError(null);
    }
  }, [isOpen]);

  // Compress image
  const compressImage = (file, maxSizeKB = 300) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          
          if (width > MAX_WIDTH) {
            height = (height * MAX_WIDTH) / width;
            width = MAX_WIDTH;
          }
          if (height > MAX_HEIGHT) {
            width = (width * MAX_HEIGHT) / height;
            height = MAX_HEIGHT;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          let quality = 0.8;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          while (dataUrl.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          resolve(dataUrl);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    setCompressing(true);
    try {
      const compressed = await compressImage(file);
      setImage(compressed);
      setImagePreview(compressed);
      toast.success("Image ready!");
    } catch {
      toast.error("Failed to process image");
    } finally {
      setCompressing(false);
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() && !image) {
      toast.error("Add some text or an image");
      return;
    }
    
    setSubmitting(true);
    try {
      await onSubmit({ 
        content, 
        image_url: image,
        visibility: visibility,
        category_tags: selectedTags
      });
      setContent('');
      setImage(null);
      setImagePreview(null);
      setVisibility('public');
      setSelectedTags([]);
      onClose();
    } catch {
      toast.error("Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg font-bold">Create Post</DialogTitle>
        </DialogHeader>
        
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-1">
          {/* User */}
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <span className="font-semibold">{user?.name}</span>
          </div>

          {/* Content - Auto-expanding textarea */}
          <Textarea
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              // Auto-resize the textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
            }}
            placeholder="What's on your mind?"
            className="bg-[var(--ps-bg-base)] border-[var(--ps-border)] text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)] min-h-[44px] max-h-[200px] resize-none overflow-hidden py-2.5"
            style={{ height: '44px' }}
            autoFocus
          />

          {/* Quick Picks - Prewritten messages */}
          {!content && (
            <div>
              <label className="text-xs font-medium text-[var(--ps-text-muted)] mb-2 block">Quick Picks</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { text: "Great day to be out on the course! Who else is playing this weekend?", icon: "⛳" },
                  { text: "Anyone up for a round this weekend?", icon: "🏌️" },
                  { text: "Finally broke my personal best today! 🔥", icon: "🎯" },
                  { text: "Course conditions are perfect right now", icon: "✨" },
                  { text: "Who wants to play a skins game?", icon: "💰" },
                ].map((pick, idx) => (
                  <button
                    key={idx}
                    onClick={() => setContent(pick.text)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-primary)]/20 text-[var(--ps-text-secondary)] hover:text-[var(--ps-primary)] text-sm transition-all border border-transparent hover:border-[var(--ps-primary)]/30"
                  >
                    <span>{pick.icon}</span>
                    <span className="truncate max-w-[200px]">{pick.text.substring(0, 30)}...</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Social media hint */}
          <p className="text-xs text-[var(--ps-text-muted)] flex items-center gap-1.5">
            <LinkIcon className="w-3 h-3" />
            Paste TikTok, Instagram, YouTube links to embed
          </p>

          {/* Image/Video Preview - Limited height on mobile */}
          {imagePreview && (
            <div className="relative">
              {/* Check if it's a video URL */}
              {(imagePreview.includes('/videos/') || imagePreview.endsWith('.mp4') || imagePreview.endsWith('.mov') || imagePreview.endsWith('.webm')) ? (
                <video 
                  src={imagePreview} 
                  className="max-h-48 sm:max-h-64 rounded-lg object-cover w-full"
                  controls
                  playsInline
                  muted
                />
              ) : (
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="max-h-48 sm:max-h-64 rounded-lg object-cover w-full"
                />
              )}
              <button
                onClick={clearImage}
                className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Social Media Embed Preview */}
          {embedLoading && (
            <div className="p-4 rounded-lg bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)]">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-[var(--ps-primary)] border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-[var(--ps-text-muted)]">Loading embed preview...</span>
              </div>
            </div>
          )}
          
          {embedPreview && !embedLoading && (
            <div className="rounded-lg overflow-hidden border border-[var(--ps-border)] bg-[var(--ps-bg-elevated)]">
              {/* Thumbnail */}
              {embedPreview.thumbnail_url && (
                <div className="relative">
                  <img 
                    src={embedPreview.thumbnail_url} 
                    alt={embedPreview.title || 'Video thumbnail'}
                    className="w-full max-h-48 object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Info */}
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-[var(--ps-primary)]/20 text-[var(--ps-primary)]">
                    {embedPreview.provider_name}
                  </span>
                </div>
                {embedPreview.title && (
                  <p className="text-sm font-medium text-[var(--ps-text-primary)] line-clamp-2">{embedPreview.title}</p>
                )}
                {embedPreview.author_name && (
                  <p className="text-xs text-[var(--ps-text-muted)] mt-1">@{embedPreview.author_name}</p>
                )}
              </div>
              
              {/* Remove embed button */}
              <button
                onClick={() => setEmbedPreview(null)}
                className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {embedError && !embedLoading && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-sm text-amber-400">{embedError}</p>
            </div>
          )}

          {/* Media Actions - Clean button row */}
          <div className="flex items-center gap-3 pt-2 border-t border-[var(--ps-border)]">
            {/* PS Golf Shots - Photo/Video with filters */}
            <button 
              onClick={() => setShowGolfShots(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--ps-bg-elevated)] hover:bg-green-500/20 text-[var(--ps-text-secondary)] hover:text-green-400 transition-colors"
            >
              <span className="text-xs font-bold text-green-500 px-1 rounded" style={{background: 'rgba(34, 197, 94, 0.2)'}}>PS</span>
              <span className="text-sm font-medium">Golf Shots</span>
            </button>
            
            {/* PS Tracer */}
            <button 
              onClick={() => setShowShotTracer(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--ps-bg-elevated)] hover:bg-yellow-500/20 text-[var(--ps-text-secondary)] hover:text-yellow-400 transition-colors"
            >
              <span className="text-xs font-bold text-yellow-400 px-1 rounded" style={{background: 'rgba(234, 179, 8, 0.2)'}}>PS</span>
              <span className="text-sm font-medium">Tracer</span>
            </button>
          </div>
          
          {/* Visibility Toggle */}
          <div className="pt-3 border-t border-[var(--ps-border)]">
            <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-2 block">Who can see this?</label>
            <div className="flex gap-2">
              <button
                onClick={() => setVisibility('public')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border transition-all ${
                  visibility === 'public'
                    ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10 text-[var(--ps-primary)]'
                    : 'border-[var(--ps-border)] text-[var(--ps-text-muted)] hover:border-[var(--ps-text-muted)]'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">Public</span>
              </button>
              <button
                onClick={() => setVisibility('buddies_only')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border transition-all ${
                  visibility === 'buddies_only'
                    ? 'border-amber-500 bg-amber-500/10 text-amber-500'
                    : 'border-[var(--ps-border)] text-[var(--ps-text-muted)] hover:border-[var(--ps-text-muted)]'
                }`}
              >
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">Buddies Only</span>
              </button>
            </div>
          </div>
          
          {/* Category Tags */}
          <div>
            <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-2 block">Add Topics (optional)</label>
            <div className="flex flex-wrap gap-2">
              {categoryTags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                    selectedTags.includes(tag.id)
                      ? 'bg-[var(--ps-primary)] text-white'
                      : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-base)]'
                  }`}
                >
                  <span>{tag.icon}</span>
                  <span>{tag.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Golf Shots Modal */}
        <GolfShotsModal 
          isOpen={showGolfShots}
          onClose={() => setShowGolfShots(false)}
          onGifCreated={(mediaData, metadata) => {
            setImage(mediaData);
            setImagePreview(mediaData);
            if (metadata?.isVideo) {
              toast.success("Video ready for posting!");
            } else {
              toast.success("Golf Shot added!");
            }
          }}
        />

        {/* Shot Tracer Modal */}
        <ShotTracerModal 
          isOpen={showShotTracer}
          onClose={() => setShowShotTracer(false)}
          onVideoCreated={(videoUrl) => {
            setImage(videoUrl);
            setImagePreview(videoUrl);
            toast.success("Shot Tracer added!");
          }}
        />

        {/* Fixed footer with Post button */}
        <DialogFooter className="flex-shrink-0 border-t border-[var(--ps-border)] pt-4 mt-2">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-[var(--ps-text-secondary)]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={(!content.trim() && !image) || submitting || compressing}
            className="bg-blue-500 hover:bg-blue-400 text-white font-semibold"
            style={{boxShadow: '0 0 12px rgba(59, 130, 246, 0.6), 0 0 24px rgba(59, 130, 246, 0.3)'}}
          >
            {submitting ? (
              <div className="dark-spinner w-4 h-4" />
            ) : (
              'Post'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Manage Feed Modal - For following/unfollowing categories
const ManageFeedModal = ({ isOpen, onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);
  
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/feed/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleCategory = async (categoryId) => {
    try {
      const response = await axios.post(`${API}/feed/categories/${categoryId}/follow`);
      setCategories(prev => prev.map(cat => 
        cat.id === categoryId 
          ? { ...cat, is_followed: response.data.is_following }
          : cat
      ));
      toast.success(response.data.is_following ? 'Category followed!' : 'Category unfollowed');
    } catch (error) {
      toast.error("Failed to update category");
    }
  };
  
  const postTypeCategories = categories.filter(c => c.type === 'post_type');
  const topicCategories = categories.filter(c => c.type === 'topic');
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <svg className="w-5 h-5 text-[var(--ps-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            Manage Your Feed
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 space-y-6 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-[var(--ps-primary)] border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {/* Info */}
              <p className="text-sm text-[var(--ps-text-muted)]">
                Follow categories to see more posts in your feed. Your feed also shows all posts from your Buddies.
              </p>
              
              {/* Post Types */}
              <div>
                <h3 className="text-sm font-semibold text-[var(--ps-text-secondary)] mb-3 uppercase tracking-wide">Post Types</h3>
                <div className="space-y-2">
                  {postTypeCategories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
                        cat.is_followed
                          ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                          : 'border-[var(--ps-border)] hover:border-[var(--ps-text-muted)]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{cat.icon}</span>
                        <span className="font-medium text-[var(--ps-text-primary)]">{cat.name}</span>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        cat.is_followed
                          ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]'
                          : 'border-[var(--ps-text-muted)]'
                      }`}>
                        {cat.is_followed && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Golf Topics */}
              <div>
                <h3 className="text-sm font-semibold text-[var(--ps-text-secondary)] mb-3 uppercase tracking-wide">Golf Topics</h3>
                <div className="space-y-2">
                  {topicCategories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
                        cat.is_followed
                          ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                          : 'border-[var(--ps-border)] hover:border-[var(--ps-text-muted)]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{cat.icon}</span>
                        <span className="font-medium text-[var(--ps-text-primary)]">{cat.name}</span>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        cat.is_followed
                          ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]'
                          : 'border-[var(--ps-text-muted)]'
                      }`}>
                        {cat.is_followed && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        
        <DialogFooter>
          <Button onClick={onClose} className="w-full bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Members Directory Component
const MembersDirectory = ({ user, onMessageUser }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/members`, {
        params: { search: searchQuery || undefined }
      });
      setMembers(response.data.members);
      setTotalCount(response.data.total_count);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const debounceTimer = setTimeout(fetchMembers, 300);
    return () => clearTimeout(debounceTimer);
  }, [fetchMembers]);

  const handleFollow = async (targetUserId, isFollowing) => {
    try {
      if (isFollowing) {
        await axios.delete(`${API}/users/${targetUserId}/follow`);
        toast.success("Unfollowed");
      } else {
        await axios.post(`${API}/users/${targetUserId}/follow`);
        toast.success("Following!");
      }
      // Update local state
      setMembers(prev => prev.map(m => 
        m.user_id === targetUserId 
          ? { ...m, is_following: !isFollowing, followers_count: isFollowing ? m.followers_count - 1 : m.followers_count + 1 }
          : m
      ));
      // Also update selected member if modal is open
      if (selectedMember?.user_id === targetUserId) {
        setSelectedMember(prev => prev ? { ...prev, is_following: !isFollowing } : null);
      }
    } catch (error) {
      toast.error("Failed to update follow status");
    }
  };
  
  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setShowProfileModal(true);
  };

  return (
    <div className="p-4 space-y-4" data-testid="members-directory">
      {/* Search Bar */}
      <div className="bg-[var(--ps-bg-surface)] rounded-2xl p-4 border border-[var(--ps-border)]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--ps-text-muted)]" />
          <Input
            type="text"
            placeholder="Search by name, username, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 w-full bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] rounded-xl text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)]"
            data-testid="member-search-input"
          />
        </div>
      </div>

      {/* Member Directory Card */}
      <div className="bg-[var(--ps-bg-surface)] rounded-2xl p-4 border border-[var(--ps-border)]">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-[var(--ps-primary)]" />
          <h2 className="font-bold text-[var(--ps-text-primary)]">Member Directory</h2>
        </div>
        <p className="text-sm text-[var(--ps-text-muted)] mb-4">{totalCount} golfers in the community</p>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="dark-spinner" />
          </div>
        ) : members.length === 0 ? (
          <p className="text-center py-8 text-[var(--ps-text-muted)]">No members found</p>
        ) : (
          <div className="space-y-2">
            {members.map((member) => (
              <button 
                key={member.user_id}
                onClick={() => handleMemberClick(member)}
                className="w-full flex items-center gap-3 p-3 bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] hover:bg-[var(--ps-bg-hover)] transition-colors text-left"
                data-testid={`member-${member.user_id}`}
              >
                {/* Avatar */}
                <UserBubble
                  userId={member.user_id}
                  picture={member.picture}
                  name={member.name}
                  bubbleColor={member.bubble_color || 'green'}
                  isOnline={member.is_online}
                  size="lg"
                />
                
                {/* User Info - Clean vertical layout */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold text-[var(--ps-text-primary)] ${getEngagementGlowClass(member)}`}>
                      {member.name}
                    </span>
                    {member.username && (
                      <span className="text-sm text-[var(--ps-text-muted)]">@{member.username}</span>
                    )}
                  </div>
                  
                  {/* Home Course - below name */}
                  {member.home_course && (
                    <p className="text-sm text-[var(--ps-text-secondary)] truncate flex items-center gap-1 mt-0.5">
                      <Flag className="w-3 h-3 text-[var(--ps-primary)] flex-shrink-0" />
                      <span className="truncate">{member.home_course}</span>
                    </p>
                  )}
                </div>
                
                {/* Handicap Badge - right aligned */}
                {member.handicap !== null && (
                  <span className="text-xs bg-[var(--ps-primary)] text-white px-2 py-1 rounded-full font-semibold flex-shrink-0">
                    {member.handicap} HCP
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        member={selectedMember}
        onFollow={handleFollow}
        onMessage={onMessageUser}
        currentUserId={user?.user_id}
      />
    </div>
  );
};

// Main Social Feed Component
export const SocialFeedPage = ({ user, subscription }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('feed');
  const [posts, setPosts] = useState([]);
  const [userStories, setUserStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [showStoryViewer, setShowStoryViewer] = useState(false);
  const [storyViewerIndex, setStoryViewerIndex] = useState(0);
  
  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const POSTS_PER_PAGE = 50;  // Increased for better infinite scroll experience
  const feedContainerRef = useRef(null);
  
  // Pre-loaded media from Quick Post button navigation
  const [preloadedMedia, setPreloadedMedia] = useState(null);
  
  // Video Reel Viewer state
  const [showVideoReel, setShowVideoReel] = useState(false);
  const [videoReelData, setVideoReelData] = useState([]);
  const [videoReelIndex, setVideoReelIndex] = useState(0);
  
  // Hook Feed overlay state
  const [showHookFeed, setShowHookFeed] = useState(false);
  
  // Image Gallery Viewer state
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryData, setGalleryData] = useState({ images: [], initialIndex: 0, postInfo: null });
  
  // Manage Feed Modal state
  const [showManageFeed, setShowManageFeed] = useState(false);
  
  // User handicap status for Qualifier indicator
  const [userHandicap, setUserHandicap] = useState(null);
  
  // PWA Install Prompt state
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  
  // Check if PWA is installed
  useEffect(() => {
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
    setIsPWAInstalled(isInstalled);
    
    // Show prompt if not installed and user hasn't dismissed it recently
    const dismissed = localStorage.getItem('pwa_prompt_dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    if (!isInstalled && daysSinceDismissed > 7) {
      // Delay showing prompt so it doesn't appear immediately
      const timer = setTimeout(() => setShowPWAPrompt(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const dismissPWAPrompt = () => {
    setShowPWAPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
  };

  // Check for create intent from URL
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setShowCreatePost(true);
    }
    // Handle action=post (shortcut)
    if (searchParams.get('action') === 'post') {
      setShowCreatePost(true);
    }
  }, [searchParams]);
  
  // Handle incoming share from PWA Share Target
  useEffect(() => {
    const sharedContent = searchParams.get('share');
    if (sharedContent) {
      try {
        const decodedContent = decodeURIComponent(sharedContent);
        // Set the preloaded content and open create post modal
        setPreloadedMedia({
          sharedText: decodedContent,
          type: 'shared_link'
        });
        setShowCreatePost(true);
        // Clear the share param from URL to prevent re-triggering
        window.history.replaceState({}, document.title, '/social');
      } catch (e) {
        console.error('Error decoding shared content:', e);
      }
    }
  }, [searchParams]);
  
  // Handle incoming media from Quick Post navigation (DarkAppShell)
  useEffect(() => {
    if (location.state?.golfShotMediaUrl) {
      setPreloadedMedia({
        url: location.state.golfShotMediaUrl,
        type: location.state.golfShotMediaType || 'video'
      });
      setShowCreatePost(true);
      // Clear the state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Listen for quickPostMedia custom event from DarkAppShell
  // Also check sessionStorage on mount for media set before navigation
  useEffect(() => {
    // Check sessionStorage for media that was set before navigation
    const storedMedia = sessionStorage.getItem('quickPostMedia');
    if (storedMedia) {
      try {
        const { mediaUrl, mediaType } = JSON.parse(storedMedia);
        if (mediaUrl) {
          setPreloadedMedia({
            url: mediaUrl,
            type: mediaType || 'image'
          });
          setShowCreatePost(true);
        }
      } catch (e) {
        console.error('Failed to parse stored media:', e);
      }
      // Clear the stored media after using it
      sessionStorage.removeItem('quickPostMedia');
    }
    
    // Also listen for event in case user is already on this page
    const handleQuickPostMedia = (event) => {
      const { mediaUrl, mediaType } = event.detail;
      if (mediaUrl) {
        setPreloadedMedia({
          url: mediaUrl,
          type: mediaType || 'image'
        });
        setShowCreatePost(true);
        // Clear sessionStorage if it was also set
        sessionStorage.removeItem('quickPostMedia');
      }
    };
    
    window.addEventListener('quickPostMedia', handleQuickPostMedia);
    return () => window.removeEventListener('quickPostMedia', handleQuickPostMedia);
  }, []);
  
  // Fetch user handicap for Qualifier indicator
  useEffect(() => {
    const fetchHandicap = async () => {
      try {
        const response = await axios.get(`${API}/handicap`);
        setUserHandicap(response.data?.handicap_index);
      } catch {
        setUserHandicap(null);
      }
    };
    fetchHandicap();
  }, []);

  // Fetch posts based on active tab
  const fetchPosts = useCallback(async (silent = false, loadMore = false) => {
    try {
      if (!silent && !loadMore) setLoading(true);
      if (loadMore) setLoadingMore(true);
      
      const skip = loadMore ? posts.length : 0;
      const response = await axios.get(`${API}/posts`, {
        params: { 
          filter: activeTab,
          limit: POSTS_PER_PAGE,
          skip: skip
        }
      });
      
      // Handle both old format (array) and new format ({ posts: [...] })
      const postsData = response.data.posts || response.data;
      const newPosts = Array.isArray(postsData) ? postsData : [];
      
      if (loadMore) {
        // Append new posts, avoiding duplicates
        const existingIds = new Set(posts.map(p => p.post_id));
        const uniqueNewPosts = newPosts.filter(p => !existingIds.has(p.post_id));
        setPosts(prev => [...prev, ...uniqueNewPosts]);
        setHasMore(newPosts.length >= POSTS_PER_PAGE);
      } else {
        setPosts(newPosts);
        setHasMore(newPosts.length >= POSTS_PER_PAGE);
        setPage(0);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      if (!silent) toast.error("Failed to load posts");
    } finally {
      if (!silent && !loadMore) setLoading(false);
      if (loadMore) setLoadingMore(false);
    }
  }, [activeTab, posts]);
  
  // Load more posts when user scrolls near bottom
  const loadMorePosts = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchPosts(true, true);
    }
  }, [fetchPosts, loadingMore, hasMore]);
  
  // Scroll detection for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;
      
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const clientHeight = document.documentElement.clientHeight;
      
      // Load more when user is within 500px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 500) {
        loadMorePosts();
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMorePosts, loadingMore, hasMore]);

  // Fetch stories
  const fetchStories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stories`);
      setUserStories(response.data);
    } catch {
      setUserStories([]);
    }
  }, []);

  // Initial fetch and polling for posts
  useEffect(() => {
    // Reset state when tab changes
    setPosts([]);
    setHasMore(true);
    setPage(0);
    fetchPosts();
    
    // Poll for new posts every 45 seconds (only refresh top, don't reset scroll position)
    const interval = setInterval(() => {
      // Silent refresh of just the first page to check for new posts
      axios.get(`${API}/posts`, {
        params: { filter: activeTab, limit: POSTS_PER_PAGE, skip: 0 }
      }).then(response => {
        const newPosts = response.data.posts || response.data;
        if (Array.isArray(newPosts) && newPosts.length > 0) {
          setPosts(prev => {
            // Merge new posts at the top without losing scroll position
            const existingIds = new Set(prev.map(p => p.post_id));
            const trulyNew = newPosts.filter(p => !existingIds.has(p.post_id));
            if (trulyNew.length > 0) {
              return [...trulyNew, ...prev];
            }
            // Update existing posts (for likes/comments count updates)
            const newPostMap = new Map(newPosts.map(p => [p.post_id, p]));
            return prev.map(p => newPostMap.get(p.post_id) || p);
          });
        }
      }).catch(() => {});
    }, 45000);
    
    return () => clearInterval(interval);
  }, [activeTab]); // Remove fetchPosts from deps to avoid infinite loop
  
  // Poll for stories every 60 seconds
  useEffect(() => {
    fetchStories();
    
    const interval = setInterval(fetchStories, 60000);
    return () => clearInterval(interval);
  }, [fetchStories]);

  // Create post
  const handleCreatePost = async (postData) => {
    try {
      const response = await axios.post(`${API}/posts`, {
        content: postData.content,
        post_type: 'general',
        image_url: postData.image_url,
        visibility: postData.visibility || 'public',
        category_tags: postData.category_tags || []
      });
      setPosts([response.data, ...posts]);
      toast.success("Post shared!");
    } catch (error) {
      console.error("Failed to create post:", error);
      toast.error("Failed to create post. Please try again.");
      throw error; // Re-throw so the modal knows it failed
    }
  };

  // Handle story created
  const handleStoryCreated = (newStory) => {
    // Refresh stories to show the new one
    fetchStories();
  };

  // Handle story deleted
  const handleStoryDeleted = (storyId) => {
    setUserStories(prev => 
      prev.map(us => ({
        ...us,
        stories: us.stories.filter(s => s.story_id !== storyId)
      })).filter(us => us.stories.length > 0)
    );
  };

  // Handle view story
  const handleViewStory = (userId) => {
    // Find the index of the user in the stories array
    const index = userStories.findIndex(us => us.user_id === userId);
    
    // Only open viewer if user exists AND has actual stories
    if (index >= 0 && userStories[index]?.stories?.length > 0) {
      setStoryViewerIndex(index);
      setShowStoryViewer(true);
    } else if (userId === user?.user_id) {
      // If it's the current user with no stories, open create modal instead
      setShowCreateStory(true);
    }
  };

  // Like post
  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/like`);
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { ...p, is_liked: response.data.liked, likes_count: response.data.likes_count }
          : p
      ));
    } catch {
      toast.error("Failed to like post");
    }
  };

  // Comment on post
  const handleComment = async (postId, content) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/comment`, { content });
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { 
              ...p, 
              comments: [...(p.comments || []), response.data], 
              comments_count: (p.comments_count || 0) + 1 
            }
          : p
      ));
      toast.success("Comment added!");
    } catch {
      toast.error("Failed to add comment");
    }
  };

  // Delete post
  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/posts/${postId}`);
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success("Post deleted");
    } catch {
      toast.error("Failed to delete post");
    }
  };

  // View profile
  const handleViewProfile = (userId) => {
    // Navigate to the user's full profile page
    navigate(`/profile/${userId}`);
  };

  // Handle video click - open video reel viewer with golf shot animation
  const handleVideoClick = (videoData) => {
    // Collect all video posts from the feed
    const videoPosts = posts
      .filter(post => {
        // Check if post has video content (YouTube, TikTok)
        const content = post.content || '';
        return content.includes('youtube.com') || 
               content.includes('youtu.be') || 
               content.includes('tiktok.com');
      })
      .map(post => {
        // Extract video info from post
        const content = post.content || '';
        let platform = 'youtube';
        let id = '';
        
        // Extract YouTube ID
        const youtubeMatch = content.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        if (youtubeMatch) {
          platform = 'youtube';
          id = youtubeMatch[1];
        }
        
        // Extract TikTok ID
        const tiktokMatch = content.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
        if (tiktokMatch) {
          platform = 'tiktok';
          id = tiktokMatch[1];
        }
        
        return {
          platform,
          id,
          url: content,
          postId: post.post_id,
          userName: post.user_name,
          content: content.replace(/https?:\/\/\S+/g, '').trim(),
          isLiked: post.is_liked,
          likesCount: post.likes_count,
          commentsCount: post.comments_count,
        };
      })
      .filter(v => v.id); // Only include posts with valid video IDs
    
    // Find index of clicked video
    const clickedIndex = videoPosts.findIndex(v => v.id === videoData.id);
    
    setVideoReelData(videoPosts);
    setVideoReelIndex(clickedIndex >= 0 ? clickedIndex : 0);
    setShowVideoReel(true);
  };

  // Handle messaging a user from Members tab - navigate to Chat page
  const handleMessageUser = async (member) => {
    // Create or get existing conversation with this user, then navigate to it
    try {
      // First check if conversation already exists
      const convosResponse = await axios.get(`${API}/conversations`);
      const existingConvo = convosResponse.data.conversations?.find(
        c => c.participants?.some(p => p.user_id === member.user_id)
      );
      
      if (existingConvo) {
        // Navigate to existing conversation
        navigate(`/chat?conversation=${existingConvo.conversation_id}`);
      } else {
        // Create new conversation
        const response = await axios.post(`${API}/conversations`, {
          participant_ids: [member.user_id]
        });
        navigate(`/chat?conversation=${response.data.conversation_id}`);
      }
    } catch (error) {
      console.error("Failed to start conversation:", error);
      // Fallback to chat page
      navigate('/chat');
    }
  };

  return (
    <div className="pb-4" data-testid="social-feed-page">
      {/* Feed Tabs with Post and Qualifier links */}
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-4">
          {feedTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`feed-tab ${activeTab === tab.id ? 'active' : ''}`}
              data-testid={`feed-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
          {/* Create Post Button - Blue glowing style */}
          <button
            onClick={() => setShowCreatePost(true)}
            className="feed-tab font-bold"
            style={{
              color: '#3b82f6',
              textShadow: '0 0 8px rgba(59, 130, 246, 0.8), 0 0 16px rgba(59, 130, 246, 0.5)'
            }}
            data-testid="create-post-btn"
          >
            Post
          </button>
          {/* Book - Tee Time Management - Yellow/Amber to match nav */}
          <button
            onClick={() => navigate('/book')}
            className="feed-tab font-bold"
            style={{
              color: '#f59e0b',
              textShadow: '0 0 8px rgba(245, 158, 11, 0.8), 0 0 16px rgba(245, 158, 11, 0.5)'
            }}
            data-testid="book-btn"
          >
            Book
          </button>
          {/* Qualifier link - Orange "Qualifying" when no handicap, Green glowing "Qualified" when established */}
          <button
            onClick={() => navigate('/dashboard')}
            className="feed-tab font-bold transition-all"
            style={{
              color: userHandicap !== null && userHandicap !== undefined
                ? '#22c55e'
                : '#f97316',
              textShadow: userHandicap !== null && userHandicap !== undefined
                ? '0 0 8px rgba(34, 197, 94, 0.6), 0 0 16px rgba(34, 197, 94, 0.4)'
                : 'none'
            }}
            data-testid="qualifier-link"
          >
            {userHandicap !== null && userHandicap !== undefined ? 'Qualified' : 'Qualifying'}
          </button>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'feed' && (
        <>
          {/* Stats Widget */}
          <div className="px-4 py-2">
            <CompactStatsWidget user={user} />
          </div>

          {/* Other Users' Stories (if any) */}
          {userStories?.filter(us => us.user_id !== user?.user_id && us.stories?.length > 0).length > 0 && (
            <div className="stories-container px-4 pb-2" style={{ paddingLeft: '16px' }}>
              {userStories?.filter(us => us.user_id !== user?.user_id && us.stories?.length > 0).map((userStory) => (
                <div 
                  key={userStory.user_id} 
                  className="story-item"
                  onClick={() => handleViewStory(userStory.user_id)}
                  data-testid={`story-${userStory.user_id}`}
                >
                  <div className={`story-avatar-wrapper ${userStory.has_unseen ? '' : 'no-story'}`}>
                    <Avatar className="story-avatar">
                      <AvatarImage src={userStory.user_picture} />
                      <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
                        {userStory.user_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <span className="story-name">{userStory.user_name?.split(' ')[0]}</span>
                </div>
              ))}
            </div>
          )}

          {/* Posts Feed */}
          <div className="p-4 space-y-4">
            {/* PWA Install Prompt - Show above posts if not installed */}
            {showPWAPrompt && !isPWAInstalled && (
              <div className="mb-4">
                <PWAInstallPrompt onDismiss={dismissPWAPrompt} />
              </div>
            )}
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="dark-spinner" />
              </div>
            ) : posts.length === 0 ? (
              <div className="dark-empty-state">
                <ImageIcon />
                <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">No posts yet</h3>
                <p className="text-[var(--ps-text-secondary)]">Be the first to share something!</p>
                <Button 
                  className="dark-btn-primary mt-4"
                  onClick={() => setShowCreatePost(true)}
                >
                  Create Post
                </Button>
              </div>
            ) : (
              posts.map((post) => (
                <PostCard
                  key={post.post_id}
                  post={post}
                  user={user}
                  onLike={handleLikePost}
                  onComment={handleComment}
                  onDelete={handleDeletePost}
                  onViewProfile={handleViewProfile}
                  onVideoClick={handleVideoClick}
                  onImageClick={(data) => {
                    // Build feed-wide gallery with all images from posts
                    const allFeedImages = posts
                      .filter(p => p.image_url)
                      .map(p => ({
                        url: p.image_url,
                        caption: p.content,
                        postId: p.post_id,
                        postInfo: {
                          author: { name: p.user_name, picture: p.user_picture },
                          likes: p.likes_count,
                          comments: p.comments_count,
                          timestamp: formatTimeAgo(p.created_at)
                        }
                      }));
                    
                    // Find the index of the clicked image
                    const clickedIndex = allFeedImages.findIndex(img => img.url === data.images[0]?.url);
                    
                    setGalleryData({
                      images: allFeedImages,
                      initialIndex: clickedIndex >= 0 ? clickedIndex : 0,
                      postInfo: allFeedImages[clickedIndex >= 0 ? clickedIndex : 0]?.postInfo
                    });
                    setShowImageGallery(true);
                  }}
                />
              ))
            )}
            
            {/* Infinite Scroll Loading Indicator */}
            {loadingMore && (
              <div className="flex justify-center py-6">
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading more posts...</span>
                </div>
              </div>
            )}
            
            {/* End of Feed Message */}
            {!hasMore && posts.length > 0 && (
              <div className="text-center py-6 text-gray-500">
                <p>You've reached the end of the feed! 🏌️</p>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'members' && (
        <MembersDirectory 
          user={user} 
          onMessageUser={handleMessageUser}
        />
      )}

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={showCreatePost}
        onClose={() => {
          setShowCreatePost(false);
          setPreloadedMedia(null);
        }}
        user={user}
        onSubmit={handleCreatePost}
        preloadedMedia={preloadedMedia}
      />

      {/* Create Story Modal */}
      <CreateStoryModal
        isOpen={showCreateStory}
        onClose={() => setShowCreateStory(false)}
        user={user}
        onStoryCreated={handleStoryCreated}
      />

      {/* Story Viewer */}
      <StoryViewer
        isOpen={showStoryViewer}
        onClose={() => setShowStoryViewer(false)}
        userStories={userStories}
        initialUserIndex={storyViewerIndex}
        currentUser={user}
        onStoryDeleted={handleStoryDeleted}
        onAddStory={() => setShowCreateStory(true)}
      />

      {/* Video Reel Viewer - Golf Shot Animation */}
      <VideoReelViewer
        isOpen={showVideoReel}
        onClose={() => setShowVideoReel(false)}
        videos={videoReelData}
        initialIndex={videoReelIndex}
        onLike={(postId) => handleLikePost(postId)}
        onComment={(postId) => {
          setShowVideoReel(false);
          // Find the post and open comments
        }}
        onShare={(postId) => {
          navigator.share?.({ 
            title: 'Par Seeker Video', 
            url: window.location.href 
          }).catch(() => {});
        }}
      />
      
      {/* Image Gallery Viewer - Fullscreen image viewer */}
      <ImageGalleryViewer
        isOpen={showImageGallery}
        onClose={() => setShowImageGallery(false)}
        images={galleryData.images}
        initialIndex={galleryData.initialIndex}
        postInfo={galleryData.postInfo}
      />
      
      {/* Manage Feed Modal - Follow/unfollow categories */}
      <ManageFeedModal
        isOpen={showManageFeed}
        onClose={() => setShowManageFeed(false)}
      />
      
      {/* Hook Feed Overlay preserved - access via /hook route */}
    </div>
  );
};

export default SocialFeedPage;
